/**
 * สิทธิ์เข้าแอปอื่น (SSO) — ตารางพนักงานทั้งหมด × แอป: ใครถือ role อะไรในแอปไหน
 * ตั้ง/เปลี่ยน role ได้ในช่องของตาราง และถอนสิทธิ์ (D-175)
 *
 * แถวคือ **ทุกคน** จาก `GET /admin/employees` (รวมคนที่ยังไม่มีสิทธิ์ใดเลย) คอลัมน์คือแอปจาก
 * `GET /admin/app-roles` และ role ที่ถืออยู่มาจาก `GET /admin/app-role-assignments`
 * ตารางเปลี่ยนได้ทางเดียวคือโหลดใหม่หลังเซิร์ฟเวอร์ตอบสำเร็จ — ไม่มี optimistic update
 *
 * ช่องเลือก role ถูกกันที่นี่ด้วย `RBAC_ADMIN` EDIT — เป็น UX เท่านั้น เซิร์ฟเวอร์ตัดสินเสมอ
 */

import { useEffect, useMemo, useState } from 'react';

import { SsoAdminFailureNotice } from './SsoAdminFailureNotice';
import { StatusBadge } from '../StatusBadge';
import { InfoHint } from '../components/InfoHint';
import { can } from '../../lib/permissions/client';
import type { PermissionsState } from '../../lib/permissions/types';
import { fetchEmployees } from '../../lib/rbacAdmin/client';
import type { Employee } from '../../lib/rbacAdmin/types';
import {
  fetchSsoApps,
  fetchSsoAssignments,
  revokeEmployeeSsoRole,
  setEmployeeSsoRole,
  type SsoAdminFailure,
} from '../../lib/ssoAdmin/client';
import {
  SSO_ADMIN_MODULE,
  type SsoApp,
  type SsoAppRoleAssignment,
} from '../../lib/ssoAdmin/types';

export interface SsoAppRolesScreenProps {
  readonly permissions: PermissionsState;
  readonly apiBaseUrl: string;
  readonly fetchImpl?: typeof fetch;
}

type LoadState<T> =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly items: readonly T[] }
  | SsoAdminFailure;

type AccessFilter = 'all' | 'has' | 'none';

interface RevokeTarget {
  readonly employee_id: string;
  readonly display_name: string;
  readonly app_code: string;
}

const PAGE_SIZE = 25;
const ACTIVE_STATUS = 'ACTIVE';

function assignmentKey(employeeId: string, appCode: string): string {
  return `${employeeId}\u0000${appCode}`;
}

export function SsoAppRolesScreen({ permissions, apiBaseUrl, fetchImpl }: SsoAppRolesScreenProps) {
  const canEdit = can(permissions, SSO_ADMIN_MODULE, 'EDIT');

  const [apps, setApps] = useState<LoadState<SsoApp>>({ status: 'loading' });
  const [employees, setEmployees] = useState<LoadState<Employee>>({ status: 'loading' });
  const [rows, setRows] = useState<LoadState<SsoAppRoleAssignment>>({ status: 'loading' });
  const [reloadToken, setReloadToken] = useState(0);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<SsoAdminFailure | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<RevokeTarget | null>(null);

  const [query, setQuery] = useState('');
  const [filterApp, setFilterApp] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [accessFilter, setAccessFilter] = useState<AccessFilter>('all');
  const [page, setPage] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    const options = { apiBaseUrl, fetchImpl, signal: controller.signal };
    void fetchSsoApps(options).then((result) => {
      if (active && result.status !== 'cancelled') {
        setApps(result);
      }
    });
    void fetchSsoAssignments({ appCode: '' }, options).then((result) => {
      if (active && result.status !== 'cancelled') {
        setRows(result);
      }
    });
    void fetchEmployees(options).then((result) => {
      if (!active || result.status === 'cancelled') {
        return;
      }
      setEmployees(
        result.status === 'ready' ? { status: 'ready', items: result.page.items } : result,
      );
    });
    return () => {
      active = false;
      controller.abort();
    };
  }, [apiBaseUrl, fetchImpl, reloadToken]);

  const reload = () => {
    setReloadToken((token) => token + 1);
  };

  const appList = apps.status === 'ready' ? apps.items : [];
  const employeeList = useMemo(
    () => (employees.status === 'ready' ? employees.items : []),
    [employees],
  );
  const assignmentList = useMemo(() => (rows.status === 'ready' ? rows.items : []), [rows]);
  const options = { apiBaseUrl, fetchImpl };

  const byEmployeeApp = useMemo(() => {
    const map = new Map<string, SsoAppRoleAssignment>();
    for (const row of assignmentList) {
      map.set(assignmentKey(row.employee_id, row.app_code), row);
    }
    return map;
  }, [assignmentList]);

  const visibleApps = filterApp === '' ? appList : appList.filter((app) => app.app_code === filterApp);

  const statuses = useMemo(
    () => Array.from(new Set(employeeList.map((employee) => employee.status))).sort(),
    [employeeList],
  );

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return employeeList.filter((employee) => {
      if (statusFilter !== '' && employee.status !== statusFilter) {
        return false;
      }
      if (needle !== '') {
        const haystack = [
          employee.employee_id,
          employee.display_name,
          employee.primary_department_name ?? '',
        ]
          .join('\n')
          .toLowerCase();
        if (!haystack.includes(needle)) {
          return false;
        }
      }
      if (accessFilter !== 'all') {
        const holds = visibleApps.some((app) =>
          byEmployeeApp.has(assignmentKey(employee.employee_id, app.app_code)),
        );
        if ((accessFilter === 'has') !== holds) {
          return false;
        }
      }
      return true;
    });
  }, [employeeList, query, statusFilter, accessFilter, visibleApps, byEmployeeApp]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageIndex = Math.min(page, pageCount - 1);
  const pageRows = filtered.slice(pageIndex * PAGE_SIZE, (pageIndex + 1) * PAGE_SIZE);

  const submitSet = async (employee: Employee, appCode: string, roleCode: string) => {
    if (!canEdit || busy || roleCode === '') {
      return;
    }
    setBusy(true);
    setFailure(null);
    setNotice(null);
    const result = await setEmployeeSsoRole(employee.employee_id, appCode, roleCode, options);
    setBusy(false);
    if (result.status === 'cancelled') {
      return;
    }
    if (result.status !== 'ready') {
      setFailure(result);
      return;
    }
    setNotice(
      `ตั้งสิทธิ์ ${result.assignment.employee_id} ในแอป ${result.assignment.app_code} เป็น ${result.assignment.role_code} แล้ว`,
    );
    reload();
  };

  const confirmRevoke = async () => {
    if (!canEdit || busy || revokeTarget === null) {
      return;
    }
    const target = revokeTarget;
    setBusy(true);
    setFailure(null);
    setNotice(null);
    const result = await revokeEmployeeSsoRole(target.employee_id, target.app_code, options);
    setBusy(false);
    if (result.status === 'cancelled') {
      return;
    }
    setRevokeTarget(null);
    if (result.status !== 'ready') {
      setFailure(result);
      return;
    }
    setNotice(`ถอนสิทธิ์ ${target.employee_id} ในแอป ${target.app_code} แล้ว`);
    reload();
  };

  const renderCell = (employee: Employee, app: SsoApp) => {
    const held = byEmployeeApp.get(assignmentKey(employee.employee_id, app.app_code));
    if (!canEdit) {
      return held === undefined ? (
        <span className="cap-field__hint">—</span>
      ) : (
        <StatusBadge tone="info">{held.role_code}</StatusBadge>
      );
    }
    const activeRoles = app.roles.filter((role) => role.active);
    const heldIsListed = held === undefined || activeRoles.some((r) => r.role_code === held.role_code);
    return (
      <select
        className="cap-input"
        aria-label={`role ของ ${employee.display_name} ในแอป ${app.app_code}`}
        data-testid={`sso-cell-${employee.employee_id}-${app.app_code}`}
        disabled={busy}
        value={held?.role_code ?? ''}
        onChange={(event) => {
          const next = event.target.value;
          if (next === '') {
            setRevokeTarget({
              employee_id: employee.employee_id,
              display_name: employee.display_name,
              app_code: app.app_code,
            });
            return;
          }
          void submitSet(employee, app.app_code, next);
        }}
      >
        <option value="">— ไม่มีสิทธิ์ —</option>
        {held !== undefined && !heldIsListed ? (
          <option value={held.role_code}>{held.role_code} (ปิดใช้งาน)</option>
        ) : null}
        {activeRoles.map((role) => (
          <option key={role.role_code} value={role.role_code}>
            {role.role_code} — {role.description}
          </option>
        ))}
      </select>
    );
  };

  const loading = employees.status === 'loading' || rows.status === 'loading';

  return (
    <div className="cap-page" data-testid="sso-app-roles">
      <div className="cap-page__head">
        <h1 className="cap-page__title">
          สิทธิ์เข้าแอปอื่น (SSO){' '}
          <InfoHint
            label="เกี่ยวกับสิทธิ์เข้าแอปอื่น"
            text="พนักงานหนึ่งคนถือได้หนึ่ง role ต่อหนึ่งแอป การตั้ง role ใหม่แทนที่ role เดิม การถอนสิทธิ์ทำให้ออกจากระบบแอปนั้นทันที"
          />
        </h1>
      </div>

      {!canEdit ? (
        <div className="cap-notice" data-testid="sso-app-roles-read-only" role="status">
          <StatusBadge tone="warning">ดูได้อย่างเดียว</StatusBadge>
          <p className="cap-notice__text">
            สิทธิ์ชุดนี้ไม่มี EDIT ของโมดูล {SSO_ADMIN_MODULE} ช่องตั้ง role จึงไม่แสดง
            (เซิร์ฟเวอร์เป็นผู้ตัดสินจริงทุกคำขอ)
          </p>
        </div>
      ) : null}

      <div className="cap-filters">
        <label className="cap-field">
          <span className="cap-field__label">ค้นหา</span>
          <input
            className="cap-input"
            data-testid="sso-filter-query"
            placeholder="รหัส ชื่อ หรือแผนก"
            autoComplete="off"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setPage(0);
            }}
          />
        </label>
        <label className="cap-field">
          <span className="cap-field__label">แอป</span>
          <select
            className="cap-input"
            data-testid="sso-filter-app"
            value={filterApp}
            onChange={(event) => {
              setFilterApp(event.target.value);
              setPage(0);
            }}
          >
            <option value="">ทุกแอป</option>
            {appList.map((app) => (
              <option key={app.app_code} value={app.app_code}>
                {app.app_code} — {app.app_name}
              </option>
            ))}
          </select>
        </label>
        <label className="cap-field">
          <span className="cap-field__label">สถานะพนักงาน</span>
          <select
            className="cap-input"
            data-testid="sso-filter-status"
            value={statusFilter}
            onChange={(event) => {
              setStatusFilter(event.target.value);
              setPage(0);
            }}
          >
            <option value="">ทั้งหมด</option>
            {statuses.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>
        </label>
        <label className="cap-field">
          <span className="cap-field__label">การถือสิทธิ์</span>
          <select
            className="cap-input"
            data-testid="sso-filter-access"
            value={accessFilter}
            onChange={(event) => {
              setAccessFilter(event.target.value as AccessFilter);
              setPage(0);
            }}
          >
            <option value="all">ทั้งหมด</option>
            <option value="has">มีสิทธิ์อย่างน้อยหนึ่งแอป</option>
            <option value="none">ยังไม่มีสิทธิ์</option>
          </select>
        </label>
      </div>

      {notice === null ? null : (
        <div className="cap-notice" data-testid="sso-notice" role="status">
          <StatusBadge tone="success">บันทึกแล้ว</StatusBadge>
          <p className="cap-notice__text">{notice}</p>
        </div>
      )}

      {failure === null ? null : <SsoAdminFailureNotice failure={failure} onRetry={reload} />}

      {canEdit && revokeTarget !== null ? (
        <section className="cap-notice" data-testid="sso-revoke-confirm" role="alertdialog">
          <StatusBadge tone="warning">ยืนยันก่อนถอน</StatusBadge>
          <h2 className="cap-notice__title">
            ถอนสิทธิ์ {revokeTarget.display_name} (
            <code className="cap-table__code">{revokeTarget.employee_id}</code>) ในแอป{' '}
            {revokeTarget.app_code}?
          </h2>
          <p className="cap-notice__text">
            เขาจะ<strong>ถูกออกจากระบบของแอปนั้นทันที</strong> ทุก session และ token
            ของแอปนี้ถูกยกเลิก และเข้าใหม่ไม่ได้จนกว่าจะตั้ง role ให้อีกครั้ง
          </p>
          <div className="cap-notice__actions">
            <button
              type="button"
              className="cap-button cap-button--primary"
              data-testid="sso-revoke-confirm-yes"
              disabled={busy}
              onClick={() => {
                void confirmRevoke();
              }}
            >
              {busy ? 'กำลังถอน…' : 'ยืนยัน ถอนสิทธิ์'}
            </button>
            <button
              type="button"
              className="cap-button cap-button--ghost"
              data-testid="sso-revoke-confirm-no"
              disabled={busy}
              onClick={() => {
                setRevokeTarget(null);
              }}
            >
              ยกเลิก
            </button>
          </div>
        </section>
      ) : null}

      {loading ? (
        <div className="cap-notice" data-testid="sso-app-roles-loading" role="status">
          <p className="cap-notice__text">กำลังโหลดรายชื่อพนักงานและสิทธิ์…</p>
        </div>
      ) : null}
      {employees.status !== 'loading' && employees.status !== 'ready' ? (
        <SsoAdminFailureNotice failure={employees} onRetry={reload} />
      ) : null}
      {rows.status !== 'loading' && rows.status !== 'ready' ? (
        <SsoAdminFailureNotice failure={rows} onRetry={reload} />
      ) : null}
      {apps.status !== 'loading' && apps.status !== 'ready' ? (
        <SsoAdminFailureNotice failure={apps} onRetry={reload} />
      ) : null}

      {employees.status === 'ready' && employeeList.length === 0 ? (
        <div className="cap-notice" data-testid="sso-app-roles-empty" role="status">
          <StatusBadge tone="neutral">ยังไม่มีพนักงาน</StatusBadge>
          <p className="cap-notice__text">
            เซิร์ฟเวอร์ตอบสำเร็จแต่รายการว่างเปล่า — ไม่ใช่การโหลดที่ล้มเหลว
          </p>
        </div>
      ) : null}

      {employees.status === 'ready' && rows.status === 'ready' && employeeList.length > 0 ? (
        <section className="cap-card cap-card--table" aria-label="สิทธิ์เข้าแอปของพนักงานทั้งหมด">
          <p className="cap-field__hint" data-testid="sso-app-roles-count">
            แสดง {filtered.length} จาก {employeeList.length} คน
          </p>
          <div className="cap-table-wrap">
            <table className="cap-table" data-testid="sso-app-roles-table">
              <thead>
                <tr>
                  <th scope="col">พนักงาน</th>
                  <th scope="col">แผนก</th>
                  <th scope="col">สถานะ</th>
                  {visibleApps.map((app) => (
                    <th scope="col" key={app.app_code}>
                      {app.app_code}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pageRows.map((employee) => (
                  <tr
                    key={employee.employee_id}
                    data-testid={`sso-employee-row-${employee.employee_id}`}
                  >
                    <th scope="row" className="cap-table__row-head">
                      {employee.display_name}
                      <br />
                      <code className="cap-table__code">{employee.employee_id}</code>
                    </th>
                    <td>{employee.primary_department_name ?? '—'}</td>
                    <td>
                      <StatusBadge tone={employee.status === ACTIVE_STATUS ? 'success' : 'neutral'}>
                        {employee.status}
                      </StatusBadge>
                    </td>
                    {visibleApps.map((app) => {
                      const held = byEmployeeApp.get(assignmentKey(employee.employee_id, app.app_code));
                      return (
                        <td key={app.app_code}>
                          {renderCell(employee, app)}
                          {held === undefined ? null : (
                            <div className="cap-field__hint">
                              ให้โดย {held.granted_by_employee_id}{' '}
                              {held.self_granted ? (
                                <span
                                  data-testid={`sso-self-granted-${employee.employee_id}-${app.app_code}`}
                                >
                                  <StatusBadge tone="warning">ให้ตัวเอง</StatusBadge>
                                </span>
                              ) : null}
                            </div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {filtered.length === 0 ? (
            <p className="cap-field__hint" data-testid="sso-app-roles-no-match">
              ไม่มีพนักงานตรงกับตัวกรอง
            </p>
          ) : null}
          {pageCount > 1 ? (
            <div className="cap-notice__actions" data-testid="sso-app-roles-pager">
              <button
                type="button"
                className="cap-button cap-button--ghost"
                data-testid="sso-page-prev"
                disabled={pageIndex === 0}
                onClick={() => {
                  setPage(pageIndex - 1);
                }}
              >
                ก่อนหน้า
              </button>
              <span className="cap-field__hint">
                หน้า {pageIndex + 1} / {pageCount}
              </span>
              <button
                type="button"
                className="cap-button cap-button--ghost"
                data-testid="sso-page-next"
                disabled={pageIndex >= pageCount - 1}
                onClick={() => {
                  setPage(pageIndex + 1);
                }}
              >
                ถัดไป
              </button>
            </div>
          ) : null}
        </section>
      ) : null}
    </div>
  );
}
